import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import AcceptInvitePage from './pages/auth/AcceptInvitePage';
import EmailResetPage from './pages/auth/EmailResetPage';
import LoginPage from './pages/auth/LoginPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import StaffInvitePage from './pages/auth/StaffInvitePage';
import DashboardShell from './components/DashboardShell';
import ClientPortalShell from './components/ClientPortalShell';
import SessionTimeout from './components/SessionTimeout';
import LanguageOnboarding from './components/LanguageOnboarding';
import { appUserService, isWorkspaceUser, type AppUserContext } from './services/appUserService';
import { authService } from './services/authService';
import { ACCOUNT_DEACTIVATED_REASON, accountDeactivatedMessage } from './utils/accountStatus';
import { clearLogoutReason, getMarketingSignInUrl } from './utils/authRedirect';
import { useI18n } from './i18n';
import './App.css';

function MarketingSignInRedirect() {
  const signInUrl = getMarketingSignInUrl();

  useEffect(() => {
    window.location.replace(signInUrl);
  }, [signInUrl]);

  return <div className="auth-loading"><div className="spinner" /></div>;
}

function DeactivatedAccountNotice() {
  const { t } = useI18n();

  return (
    <div className="auth-layout">
      <div className="auth-card" role="alert">
        <h1 className="auth-card__title">{t('Account deactivated')}</h1>
        <p className="auth-card__subtitle">{accountDeactivatedMessage()}</p>
        <button
          type="button"
          className="btn btn--secondary btn--full-width"
          onClick={() => window.location.assign(getMarketingSignInUrl(ACCOUNT_DEACTIVATED_REASON))}
        >
          {t('Back to sign in')}
        </button>
      </div>
    </div>
  );
}

function AuthGate() {
  const { t } = useI18n();
  const [session, setSession] = useState<Session | null | undefined>(undefined); // undefined = loading
  const [userContext, setUserContext] = useState<AppUserContext | null | undefined>(undefined);
  // Latched: signing the dead session out clears `session`, and the notice has
  // to outlive that rather than give way to the sign-in redirect.
  const [isDeactivated, setIsDeactivated] = useState(false);

  useEffect(() => {
    const loadContext = async (nextSession: Session | null) => {
      if (!nextSession?.user.id) {
        setUserContext(null);
        return;
      }

      const context = await appUserService.getCurrentUserContext(nextSession.user.id);
      if (context?.kind === 'deactivated') {
        setIsDeactivated(true);
        void authService.signOut().catch(() => undefined);
      }
      setUserContext(context);
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      void loadContext(s);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      void loadContext(s);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      clearLogoutReason();
    }
  }, [session]);

  if (isDeactivated) {
    return <DeactivatedAccountNotice />;
  }

  if (session === undefined || userContext === undefined) {
    return <div className="auth-loading"><div className="spinner" /></div>;
  }

  if (!session) {
    return <MarketingSignInRedirect />;
  }

  if (isWorkspaceUser(userContext?.kind)) {
    return (
      <>
        <SessionTimeout session={session} />
        <LanguageOnboarding session={session}>
          <DashboardShell
            userRole={userContext!.kind === 'manager' ? 'manager' : 'admin'}
            access={userContext!.access}
          />
        </LanguageOnboarding>
      </>
    );
  }

  if (userContext?.kind === 'client') {
    return (
      <>
        <SessionTimeout session={session} />
        <LanguageOnboarding session={session}>
          <ClientPortalShell />
        </LanguageOnboarding>
      </>
    );
  }

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <h1 className="auth-card__title">{t('Portal access is limited')}</h1>
        <p className="auth-card__subtitle">
          {t('This web workspace is for staff with a management role and invited client users. Guard accounts should continue using the field app.')}
        </p>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/sign-in" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/staff-invite" element={<StaffInvitePage />} />
        <Route path="/email-reset" element={<EmailResetPage />} />
        <Route path="/dashboard" element={<AuthGate />} />
        <Route path="/" element={<AuthGate />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
